﻿import {isNullOrEmpty, translation} from "@2d-front-monorepo/core";
import {
	initializeMvvmContext,
	Message,
	MvvmButton,
	MvvmDataProvider,
	MvvmNumberInput,
	MvvmSelect,
	MvvmTextInput
} from "@2d-front-monorepo/design-library";
import {PermitsMvvmErrorCollector, VehicleId, Vehicles} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import {JSX, useCallback} from "react";
import {useNavigate, useParams} from "react-router";
import {PermitsBackButton} from "../../components/PermitsBackButton";
import {reloadPermits} from "../../contexts/PermitsContext";
import {useGlobalContext} from "../../hooks/useGlobalContext";
import {useHttpClient} from "../../hooks/useHttpClient";
import {usePermits} from "../../hooks/usePermits";
import {PrivateRoutes} from "../../routes";

type VehicleEditModel = {
	plate: string;
	nickname?: string | undefined;
	color?: string | undefined;
	model?: string | undefined;
	type?: number | undefined;
	province?: string | undefined;
}

export default function EditVehicle(): JSX.Element {
	const {vehicleId} = useParams<{ vehicleId: string }>() as { vehicleId: VehicleId };
	const {permits} = usePermits();
	const navigate = useNavigate();
	const vehicle = isNullOrEmpty(vehicleId) ? undefined : Vehicles.get(permits, vehicleId);
	const {installation} = useGlobalContext();
	const vehicleData = installation.configuration.vehicleData;

	const context = initializeMvvmContext({
		initialState: (): VehicleEditModel => {
			const type = vehicle?.data?.type;
			return {
				plate: vehicle?.plate ?? "",
				nickname: vehicle?.data?.nickname,
				color: vehicle?.data?.color,
				model: vehicle?.data?.model,
				type: type == null ? undefined : parseInt(type, 10),
				province: vehicle?.data?.province
			};
		}
	});

	const onSubmit = useCallback(async (m: VehicleEditModel) => {
		const httpClient = useHttpClient()
		await httpClient.vehicles.update(vehicleId, {
			nickname: m.nickname,
			color: m.color,
			model: m.model,
			type: m.type?.toString(),
			province: m.province
		});

		await reloadPermits();
		navigate(PrivateRoutes.vehicles);
	}, []);

	if (vehicle == null) {
		return (<Message title={{text: `Invalid vehicle id: [${vehicleId}]`}} type="error"/>);
	}

	return <MvvmDataProvider<VehicleEditModel> context={context} onSubmit={onSubmit}>
		<h1>{translation(PermitTranslations.vehicles_page__title__edit)}</h1>
		<hr/>
		<PermitsMvvmErrorCollector/>
		<div className="layout vertical gap">
			<div className="columns gap">
				<MvvmTextInput context={context}
				               property="plate"
				               type="text"
				               column="col25"
				               disabled={true}
				               label={translation(PermitTranslations.new_vehicle__plate)}/>

				<MvvmTextInput context={context}
				               property="nickname"
				               type="text"
				               column="col25"
				               label={translation(PermitTranslations.new_vehicle__nickname)}/>

				<MvvmSelect context={context}
				            property="color"
				            items={vehicleData.colors}
				            column="col25"
				            validation={{
					            required: translation(PermitTranslations.validation__vehicle__color__required)
				            }}
				            label={translation(PermitTranslations.new_vehicle__color)}
				            placeholder={translation(PermitTranslations.vehicle__color__placeholder)}/>

				<MvvmSelect context={context}
				            property="model"
				            items={vehicleData.models}
				            column="col25"
				            validation={{
					            required: translation(PermitTranslations.validation__vehicle__model__required)
				            }}
				            label={translation(PermitTranslations.new_vehicle__model)}
				            placeholder={translation(PermitTranslations.vehicle__model__placeholder)}/>

				<MvvmNumberInput context={context}
				                 property="type"
				                 min={1900}
				                 max={2099}
				                 step={1}
				                 column="col25"
				                 placeholder="YYYY"
				                 validation={{
					                 required: translation(PermitTranslations.validation__vehicle__type__required)
				                 }}
				                 label={translation(PermitTranslations.new_vehicle__type)}/>

				<MvvmSelect context={context}
				            property="province"
				            items={vehicleData.provinces}
				            column="col25"
				            validation={{
					            required: translation(PermitTranslations.validation__vehicle__province__required)
				            }}
				            label={translation(PermitTranslations.new_vehicle__province)}
				            placeholder={translation(PermitTranslations.vehicle__province__placeholder)}/>
			</div>
		</div>
		<div className="form__buttons form__buttons--bottom">
			<MvvmButton kind="primary">{translation(PermitTranslations.save_changes)}</MvvmButton>
			<span></span>
			<PermitsBackButton/>
		</div>
	</MvvmDataProvider>;
}
